import { Logger } from "./Logger";

export interface HttpRequestConfig {
  headers?: Record<string, string>;
  timeout?: number;
}

export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/**
 * Fetch API wrapper with timeout, JSON serialization, and error handling.
 * Mirrors Android DefaultNetworkService HTTP layer.
 */
export class HttpClient {
  static async get<T>(url: string, config?: HttpRequestConfig): Promise<T> {
    return HttpClient.request<T>(url, { method: "GET", ...config });
  }

  static async post<T>(url: string, body?: unknown, config?: HttpRequestConfig): Promise<T> {
    return HttpClient.request<T>(url, { method: "POST", body, ...config });
  }

  private static async request<T>(
    url: string,
    options: { method: string; body?: unknown } & HttpRequestConfig,
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutMs = options.timeout ?? 30_000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const headers: Record<string, string> = {
        ...options.headers,
      };

      // Only set Content-Type for requests with a body (POST/PUT/PATCH).
      // Adding it to GET triggers CORS preflight on cross-origin APIs.
      if (options.body !== undefined) {
        headers["Content-Type"] = "application/json";
      }

      const fetchOptions: RequestInit = {
        method: options.method,
        headers,
        signal: controller.signal,
      };

      if (options.body !== undefined) {
        fetchOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        throw new HttpError(`HTTP Error ${response.status}: ${response.statusText}`, response.status);
      }

      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        return (await response.json()) as T;
      }
      return (await response.text()) as unknown as T;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new HttpError(`Request timeout after ${timeoutMs}ms`, 408);
      }
      Logger.error("HTTP request failed:", error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
